/**
 * Readable market-pick labels for the create flow (specs/challenge-flow-redesign — item #6 / AC-4.1).
 *
 * Handicap / over-under picks store only a bare `value` ('Home', 'Over') on the slip; the *line*
 * (+1, 2.5) lives in the marketId (merged handicap 31–39) or is already embedded in the value (O/U,
 * legacy handicap "Home -1"). This derives a legible side label so the deck picks-chip and the Review
 * row read clearly ("Home +1" instead of a bare "Home"). Pure display transform of (marketId, value) —
 * the slip's canonical `value` / `marketName` (the POST /challenges payload fields) are never mutated.
 */

import { getHandicapLineFromSlip } from '../constants/matchDetails.constants';

/**
 * Mirrors `getHandicapOptionDisplayLabel` in `overUnderGrouping.helper` (kept here to avoid a
 * src → components import). Away flips the sign so both fighters read from their own perspective.
 */
function handicapOutcomeLabel(line: number, outcome: string): string {
  if (line === 0) return `${outcome} 0`;
  const displayLine = outcome.toLowerCase() === 'away' ? -line : line;
  const sign = displayLine > 0 ? '+' : '';
  return `${outcome} ${sign}${displayLine}`;
}

/**
 * Legible side value for an "other market" slip. Adds the handicap line when the stored value is a
 * bare Home/Draw/Away on a handicap market; otherwise returns the value unchanged (O/U values already
 * carry their line, and non-handicap Home/Draw/Away markets — 1H/2H winner — get no false line).
 */
export function readablePickValue(marketId: number | undefined, value: string | undefined): string {
  const outcome = (value ?? '').trim();
  if (outcome === 'Home' || outcome === 'Draw' || outcome === 'Away') {
    const line = marketId != null ? getHandicapLineFromSlip(marketId, value) : null;
    if (line != null) return handicapOutcomeLabel(line, outcome);
  }
  return value ?? '';
}
